import { useTranslation } from '@fluxora/i18n';

import { usePricePointsQuery } from '../api/plans.queries';
import { headlineRateAt } from '../services/planPricing';
import { withheldReasonKey } from '../services/price-disclosure.service';

/**
 * THE shelf/detail headline number (E-SF02, §25.475). This is the ONLY component
 * that renders "the price", and it is fed exclusively by the price-points
 * endpoint — never the raw energy rate. It fetches its own plan's price points so
 * a card can headline the average price independently.
 *
 * ── THE USAGE IS AN ARBITRARY kWh, NOT ONE OF THREE ────────────────────────
 * This prop used to be the `ReferenceUsage` union (500 | 1000 | 2000). That type
 * was the defect: the meter-first flow prices at whatever the shopper's meter says
 * (1400 kWh/mo is normal), so the plan-detail page could not pass its real usage
 * and passed the 1000 kWh constant instead — headlining a different price from the
 * shelf card the shopper had just clicked. `headlineRateAt` owns the rest: a
 * reference level still returns the disclosed EFL string verbatim.
 *
 * All five async states are handled: idle is not reachable (the id is always
 * present here), so loading / error / success are rendered.
 *
 * ── "NO PRICE" IS NOW TWO DIFFERENT THINGS, AND THEY SAY DIFFERENT THINGS ──
 * This component used to render one blank for both, and that was the bug an agent
 * hit in production: they composed an offer for 4Change Maxx Saver Value 12, the
 * customer opened the link, and the card read "Price unavailable" — the same words
 * it shows when the service is down. The plan was fine. Pricing simply declines to
 * publish a §25.475 average price for a plan advertising a bill credit its
 * calculation does not model, because publishing one would OVERSTATE the price
 * (§25.475(c)(1)(A)). 8 of the live 44 tariffs are in that position.
 *
 * So the success branch is now a two-way switch:
 *   - PUBLISHED → the disclosed average price, exactly as before.
 *   - WITHHELD  → a plain statement that we do not publish one for this plan and
 *                 why, sitting where the number would be. This is the EMPTY state
 *                 for a price: a settled answer that there is no figure, distinct
 *                 from the error state's "we could not get one".
 * `isError` keeps the honest "unavailable" for the cases a retry can still fix.
 *
 * 🚫 NEITHER BRANCH INVENTS A NUMBER. There is no fallback to the energy charge
 * and no second endpoint to try — a figure Pricing refuses is not a gap to fill.
 */
export interface PlanPriceHeadlineProps {
  readonly planId: string;
  /** The monthly kWh to price at — any positive figure, not just an EFL level. */
  readonly monthlyKwh: number;
  /**
   * 'full' (default) renders the withheld-price explainer paragraph inline.
   * 'compact' renders only the label + headline — for the card FRONT, whose
   * back face now carries the explainer. The disclosure itself is never
   * dropped: every compact caller must put the full text one tap away.
   */
  readonly detail?: 'full' | 'compact';
  /** Card fronts centre the price under a centred name; detail pages stay left. */
  readonly align?: 'left' | 'center';
}

export function PlanPriceHeadline({
  planId,
  monthlyKwh,
  detail = 'full',
  align = 'left',
}: PlanPriceHeadlineProps): JSX.Element {
  const { t } = useTranslation();
  const query = usePricePointsQuery(planId);

  if (query.isPending) {
    return (
      <span className="text-sm text-foreground/60" data-testid="price-loading">
        {t('shop.card.avgPrice.loading')}
      </span>
    );
  }

  if (query.isError || query.data === undefined) {
    // Fail loud, but keep the card usable: an honest "unavailable", never a
    // fabricated or fallback price.
    return (
      <span className="text-sm text-danger" role="status" data-testid="price-unavailable">
        {t('shop.card.avgPrice.unavailable')}
      </span>
    );
  }

  if (query.data.status === 'WITHHELD') {
    return (
      <span
      className={`flex flex-col gap-1 ${align === 'center' ? 'items-center text-center' : ''}`}
      data-testid="price-withheld"
    >
        <span className="text-xs uppercase tracking-wide text-foreground/60">
          {t('shop.card.avgPrice.label')}
        </span>
        <span className="text-base font-semibold text-foreground">
          {t('shop.card.avgPrice.withheld.headline')}
        </span>
        {detail === 'full' ? (
          <span className="text-xs leading-snug text-foreground/70">
            {t(withheldReasonKey(query.data.reason))}
          </span>
        ) : null}
      </span>
    );
  }

  const value = headlineRateAt(query.data.pricePoints, monthlyKwh);

  return (
    <span
      className={`flex flex-col ${align === 'center' ? 'items-center text-center' : ''}`}
      data-testid="price-headline"
    >
      <span className="text-xs uppercase tracking-wide text-foreground/60">
        {t('shop.card.avgPrice.label')}
      </span>
      <span className="text-2xl font-semibold text-primary">
        {value}
        <span className="ml-1 text-sm font-normal text-foreground/70">
          {t('shop.card.avgPrice.unit')}
        </span>
      </span>
      <span className="text-xs text-foreground/60">
        {t('shop.card.avgPrice.at', { kwh: monthlyKwh })}
      </span>
    </span>
  );
}
